import React, { useState, useEffect } from 'react';

const NegotiatorGame = ({ 
  gameId, 
  playerId, 
  players: initialPlayers, 
  gameState, 
  socket,
  onBackToMenu,
  initialMap
}) => {
  // ==========================================
  // 1. STATE MANAGEMENT
  // ==========================================
  const [selectedTerritory, setSelectedTerritory] = useState(null);
  const [mapNodes, setMapNodes] = useState([]);
  const [gamePhase, setGamePhase] = useState('LOBBY'); // LOBBY -> SETUP -> PLAYING
  const [players, setPlayers] = useState(initialPlayers || []);
  const [myTerritories, setMyTerritories] = useState([]);
  const [armiesToPlace, setArmiesToPlace] = useState(6); 
  const [setupComplete, setSetupComplete] = useState(false);
  const [allPlayersReady, setAllPlayersReady] = useState(false);
  const [gameStarting, setGameStarting] = useState(false);
  
  // Resolution Phase State
  const [resolutionView, setResolutionView] = useState('orders'); // 'orders' or 'results'
  // Store resolved state for results view
  const [resolvedMapNodes, setResolvedMapNodes] = useState([]);

  // Orders State
  const [myOrders, setMyOrders] = useState([]);
  const [pendingOrder, setPendingOrder] = useState(null); 
  const [pendingOrderAmount, setPendingOrderAmount] = useState(1);
  const [selectedOrderId, setSelectedOrderId] = useState(null); 

  const [hoveredNode, setHoveredNode] = useState(null);


  // Phase Timer State
  const gamePhases = [
    { name: 'PRODUCTION', duration: 60 },
    { name: 'DIPLOMACY', duration: 180 },
    { name: 'ORDERS', duration: 180 },
    { name: 'RESOLUTION', duration: 60 }
  ];
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [phaseTimeLeft, setPhaseTimeLeft] = useState(gamePhases[0].duration);

  const MIN_NEGOTIATOR_PLAYERS = 1;

  // ==========================================
  // 2. HELPER COMPONENTS (RESOLUTION)
  // ==========================================
  
  // Render toggle for RESOLUTION phase
  const renderResolutionToggle = () => (
    <div style={{ display: 'flex', gap: '10px', margin: '10px', position: 'absolute', top: 10, right: 10, zIndex: 100 }}>
      <button
        onClick={() => setResolutionView('orders')}
        style={{
          padding: '8px 16px',
          backgroundColor: resolutionView === 'orders' ? '#1976d2' : '#eee',
          color: resolutionView === 'orders' ? 'white' : '#333',
          border: '1px solid #ccc',
          borderRadius: '4px',
          cursor: 'pointer',
          fontWeight: resolutionView === 'orders' ? 'bold' : 'normal',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}
      >
        View Orders
      </button>
      <button
        onClick={() => setResolutionView('results')}
        style={{
          padding: '8px 16px',
          backgroundColor: resolutionView === 'results' ? '#388e3c' : '#eee',
          color: resolutionView === 'results' ? 'white' : '#333',
          border: '1px solid #ccc',
          borderRadius: '4px',
          cursor: 'pointer',
          fontWeight: resolutionView === 'results' ? 'bold' : 'normal',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}
      >
        View Results
      </button>
    </div>
  );

  // Render map overlays for orders/results in RESOLUTION phase
  const renderResolutionMapOverlay = () => {
    if (resolutionView === 'orders') {
      return (
        <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 99, pointerEvents: 'none' }}>
          <div style={{ color: '#1976d2', background: 'rgba(255,255,255,0.9)', padding: '4px 8px', borderRadius: 4, fontWeight: 'bold', border: '1px solid #1976d2' }}>
            👁️ Viewing Submitted Orders
          </div>
        </div>
      );
    } else {
      return (
        <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 99, pointerEvents: 'none' }}>
          <div style={{ color: '#388e3c', background: 'rgba(255,255,255,0.9)', padding: '4px 8px', borderRadius: 4, fontWeight: 'bold', border: '1px solid #388e3c' }}>
            ⚔️ Viewing Resolution Results
          </div>
        </div>
      );
    }
  };

  // ==========================================
  // 3. EVENT HANDLERS
  // ==========================================

  const handleDoneClick = () => {
    if (socket && gameId && playerId) {
      socket.emit('playerDone', { gameId, playerId });
      console.log('✅ Done button clicked and event emitted');
    } else {
      console.log('✅ Done button clicked (no socket/gameId/playerId)');
    }
  };

  // ==========================================
  // 4. EFFECTS
  // ==========================================

  useEffect(() => {
    setPlayers(initialPlayers || []); 
    
    const handleGameUpdate = (data) => {
      setMapNodes(data.nodes || []);
      setGamePhase(data.phase || 'PRODUCTION');
      // If in RESOLUTION phase, also update resolvedMapNodes for results view
      if (data.phase === 'RESOLUTION' && data.resolvedNodes) {
        setResolvedMapNodes(data.resolvedNodes);
      }
    };

    const handleOrdersUpdate = (data) => {
      setMyOrders(data.orders || []);
    };

    const handlePlayerJoined = (data) => {
      if (data.players) setPlayers(data.players);
    };

    const handleAllPlayersReady = (data) => {
      const nextPhase = data.phase || 'PRODUCTION';
      setGamePhase(nextPhase);
      setSetupComplete(true);
      setAllPlayersReady(true);
      
      const phaseDef = gamePhases.find(p => p.name === nextPhase);
      if (phaseDef) {
        setPhaseTimeLeft(phaseDef.duration);
        setCurrentPhaseIndex(gamePhases.indexOf(phaseDef));
      }
    };

    const handleWaitingForPlayers = (data) => console.log('⏳ Waiting:', data);
    
    const handleError = (message) => {
      console.log('🚫 Error:', message);
      setGameStarting(false);
    };

    const handleGameReset = (data) => {
      setGamePhase('LOBBY');
      setSetupComplete(false);
      setAllPlayersReady(false);
      setMyTerritories([]);
      setMapNodes([]);
      setMyOrders([]);
      setResolvedMapNodes([]);
      if (data.players) setPlayers(data.players);
    };

    const handleStartFailed = (data) => {
      setGameStarting(false);
      if (data?.reason) alert(data.reason);
    };

    socket.on('negotiatorGameUpdate', handleGameUpdate);
    socket.on('negotiatorOrdersUpdate', handleOrdersUpdate);
    socket.on('playerJoined', handlePlayerJoined);
    socket.on('negotiatorAllPlayersReady', handleAllPlayersReady);
    socket.on('negotiatorWaitingForPlayers', handleWaitingForPlayers);
    socket.on('negotiatorGameReset', handleGameReset);
    socket.on('error', handleError);
    socket.on('negotiatorStartFailed', handleStartFailed);

    socket.on('negotiatorPhaseAdvanced', ({ newPhase, phaseIndex }) => {
      setGamePhase(newPhase);
      setCurrentPhaseIndex(phaseIndex);
      const phaseDef = gamePhases.find(p => p.name === newPhase);
      if (phaseDef) setPhaseTimeLeft(phaseDef.duration);
    });

    // Listen for resolved state update (if sent separately)
    socket.on('negotiatorResolutionResults', (data) => {
      if (data && data.resolvedNodes) {
        setResolvedMapNodes(data.resolvedNodes);
      }
    });

    return () => {
      socket.off('negotiatorGameUpdate', handleGameUpdate);
      socket.off('negotiatorOrdersUpdate', handleOrdersUpdate);
      socket.off('playerJoined', handlePlayerJoined);
      socket.off('negotiatorAllPlayersReady', handleAllPlayersReady);
      socket.off('negotiatorWaitingForPlayers', handleWaitingForPlayers);
      socket.off('negotiatorGameReset', handleGameReset);
      socket.off('error', handleError);
      socket.off('negotiatorStartFailed', handleStartFailed);
      socket.off('negotiatorPhaseAdvanced');
      socket.off('negotiatorResolutionResults');
    };
  }, [socket, initialPlayers]);

  // Initial Map Setup
  useEffect(() => {
    if (gameState === 'playing' && gamePhase === 'LOBBY') {
      if (!initialMap || initialMap.length === 0) {
        setGameStarting(false);
        return;
      }

      setGameStarting(false);
      const generatedMap = initialMap.map(node => ({ ...node }));
      const myAssignedTerritories = generatedMap
        .filter(t => t.owner === playerId)
        .map(t => t.id);

      setGamePhase('SETUP');
      setMyTerritories(myAssignedTerritories);
      setMapNodes(generatedMap);
      setArmiesToPlace(Math.max(0, 9 - myAssignedTerritories.length));
    }
  }, [gameState, gamePhase, playerId, initialMap]);

  useEffect(() => {
    setPlayers(initialPlayers || []);
  }, [initialPlayers]);

  // Phase Timer
  useEffect(() => {
    if (gameState !== 'playing' || gamePhase === 'LOBBY' || gamePhase === 'SETUP') return;

    const timer = setInterval(() => {
      setPhaseTimeLeft(prev => {
        if (prev <= 1) {
          advancePhase();
          return gamePhases[(currentPhaseIndex + 1) % gamePhases.length].duration;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameState, gamePhase, currentPhaseIndex]);

  const advancePhase = () => {
    if (gamePhase === 'LOBBY' || gamePhase === 'SETUP') return;

    const nextPhaseIndex = (currentPhaseIndex + 1) % gamePhases.length;
    const nextPhase = gamePhases[nextPhaseIndex];
    
    setCurrentPhaseIndex(nextPhaseIndex);
    setGamePhase(nextPhase.name);
    setPhaseTimeLeft(nextPhase.duration);
    
    socket.emit('negotiatorPhaseAdvance', {
      gameId,
      newPhase: nextPhase.name,
      phaseIndex: nextPhaseIndex
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const PHASE_UI = {
    PRODUCTION: { color: '#6a1b9a', icon: '🏭' },
    DIPLOMACY: { color: '#1565c0', icon: '🕊️' },
    ORDERS: { color: '#ef6c00', icon: '📝' },
    RESOLUTION: { color: '#2e7d32', icon: '⚔️' },
  };
  const getPhaseColor = (name) => PHASE_UI[name]?.color ?? '#666';
  const getPhaseIcon  = (name) => PHASE_UI[name]?.icon  ?? '⏰';

  // ==========================================
  // 5. GAME LOGIC HANDLERS
  // ==========================================

  const handleTerritoryClick = (territoryId) => {
    // PRODUCTION PHASE: Inspection only
    if (gamePhase === 'PRODUCTION') {
      setSelectedTerritory(territoryId);
      return;
    }
    // SETUP PHASE: Placement
    if (gamePhase === 'SETUP') {
      if (!myTerritories.includes(territoryId)) return;
      if (armiesToPlace <= 0) return;

      setSelectedTerritory(territoryId);
      setMapNodes(prev => prev.map(territory => {
        if (territory.id === territoryId) {
          return { ...territory, armies: territory.armies + 1 };
        }
        return territory;
      }));
      setArmiesToPlace(prev => prev - 1);
      return;
    }

    // MAIN PHASES: Orders
    if (pendingOrder && pendingOrder.source && !pendingOrder.target) {
      setPendingOrder(prev => ({ ...prev, target: territoryId }));
      return;
    }
    if (pendingOrder) return;

    setSelectedTerritory(territoryId);
    if (myTerritories.includes(territoryId)) {
      if (pendingOrder && pendingOrder.source !== territoryId) {
        setPendingOrder(null);
      }
    }
  };

  const handleCreateOrder = (type) => {
    if (!selectedTerritory) return;
    const newOrder = {
      id: `order-${Date.now()}`,
      source: selectedTerritory,
      type: type,
      target: null 
    };

    if (type === 'HOLD' || type === 'BUILD') {
      setMyOrders(prev => [...prev.filter(o => o.source !== selectedTerritory), newOrder]);
      setPendingOrder(null);
    } else {
      setPendingOrder(newOrder);
    }
  };

  const confirmPendingOrder = () => {
    if (!pendingOrder || (pendingOrder.type === 'MOVE' && !pendingOrder.target)) return;
    setMyOrders(prev => [
      ...prev,
      { ...pendingOrder, amount: pendingOrderAmount }
    ]);
    setPendingOrder(null);
    setPendingOrderAmount(1);
    setSelectedTerritory(null);
  };

  const cancelPendingOrder = () => {
    setPendingOrder(null);
  };

  const deleteOrder = (orderId) => {
    setMyOrders(prev => prev.filter(o => o.id !== orderId));
  };

  function getMaxMovableArmies(sourceId) {
    const territory = mapNodes.find(n => n.id === sourceId);
    if (!territory) return 1;
    const totalArmies = territory.armies || 1;
    const assigned = myOrders.filter(o => o.source === sourceId && ['MOVE','SUPPORT'].includes(o.type) && o.id !== pendingOrder?.id).reduce((sum, o) => sum + (o.amount || 1), 0);
    return Math.max(1, totalArmies - assigned - 1);
  }

  const FACTION_COLORS = {
    'faction-1': '#FF5722',
    'faction-2': '#E91E63',
    'faction-3': '#9C27B0',
    'faction-4': '#3F51B5',
    'faction-5': '#00BCD4',
    'faction-6': '#4CAF50',
    'faction-7': '#FFC107',
    'faction-8': '#795548',
  };

  const getTerritoryColor = (territory) => {
    if (!territory.owner) return '#9e9e9e'; 
    const owner = players.find(p => p.id === territory.owner);
    if (owner && owner.color) {
      if (FACTION_COLORS[owner.color]) return FACTION_COLORS[owner.color];
      return owner.color;
    }
    if (territory.owner === playerId) return '#2196f3'; 
    return '#f44336'; 
  };

  // ==========================================
  // 6. RENDER LOBBY
  // ==========================================
  
  if (gameState === 'waiting' && gamePhase === 'LOBBY') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '20px' }}>
        <div style={{ fontSize: '24px', color: '#666' }}>
          {gameState === 'waiting' ? 'Waiting for game to start...' : 'Game lobby - Preparing to start...'}
        </div>
        <div style={{ padding: '20px', backgroundColor: '#fff3e0', border: '2px solid #ff9800', borderRadius: '10px', textAlign: 'center' }}>
          <h4 style={{ color: '#ff9800', margin: '0 0 10px 0' }}>🎮 NEGOTIATOR Game</h4>
          <p style={{ margin: '5px 0', color: '#666' }}>Players: {players.length}/7</p>
          <p style={{ margin: '5px 0', color: '#666' }}>Game ID: <strong>{gameId}</strong></p>
          
          <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f0f8ff', border: '1px solid #4CAF50', borderRadius: '8px' }}>
            <p style={{ margin: '5px 0', fontSize: '14px', fontWeight: 'bold' }}>📎 Share this game:</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input 
                type="text" 
                value={`${window.location.origin}?join=${gameId}`}
                readOnly
                style={{ flex: 1, padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fff' }}
              />
              <button
                onClick={() => {
                  const gameUrl = `${window.location.origin}?join=${gameId}`;
                  navigator.clipboard.writeText(gameUrl).then(() => alert('Copied!')).catch(() => alert('Copy failed'));
                }}
                style={{ padding: '6px 12px', fontSize: '12px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                📋 Copy
              </button>
            </div>
          </div>
          
          {gameState === 'waiting' && (
            <button
              onClick={() => {
                if (gameStarting) return;
                if (players.length < MIN_NEGOTIATOR_PLAYERS) {
                  alert(`Need at least ${MIN_NEGOTIATOR_PLAYERS} players.`);
                  return;
                }
                setGameStarting(true);
                socket.emit('startNegotiatorGame', { gameId });
              }}
              disabled={gameStarting || players.length < MIN_NEGOTIATOR_PLAYERS}
              style={{
                padding: '12px 24px',
                fontSize: '16px',
                backgroundColor: (gameStarting || players.length < MIN_NEGOTIATOR_PLAYERS) ? '#ccc' : '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: (gameStarting || players.length < MIN_NEGOTIATOR_PLAYERS) ? 'not-allowed' : 'pointer',
                marginTop: '15px',
                fontWeight: 'bold'
              }}
            >
              {gameStarting ? '🔄 Starting...' : `🚀 Start Game (${players.length}/${MIN_NEGOTIATOR_PLAYERS} ready)`}
            </button>
          )}

          <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button 
              onClick={() => { socket.emit('leaveGame', { gameId }); onBackToMenu(); }}
              style={{ padding: '10px 20px', fontSize: '16px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              ❌ Leave Game
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ==========================================
  // 7. RENDER MAIN GAME
  // ==========================================

  const activePhases = ['SETUP', 'PLAYING', 'PRODUCTION', 'DIPLOMACY', 'ORDERS', 'RESOLUTION'];
  if (!activePhases.includes(gamePhase)) {
    return <div style={{ padding: '20px', textAlign: 'center' }}><h3>Loading game phase: {gamePhase}...</h3></div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100%', overflow: 'hidden' }}>
      
      {/* Legend */}
      <div style={{ display: 'flex', gap: '15px', padding: '10px', backgroundColor: '#fff', borderBottom: '1px solid #ddd', overflowX: 'auto' }}>
        {players.map(p => (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: FACTION_COLORS[p.color] || p.color || '#999' }} />
            <span style={{ fontSize: '12px', fontWeight: p.id === playerId ? 'bold' : 'normal' }}>
              {p.name} {p.id === playerId ? '(You)' : ''}
            </span>
          </div>
        ))}
      </div>

      <div style={{ flex: 1, display: 'flex' }}>
        
        {/* Map Area */}
        <div style={{ flex: 3, position: 'relative', backgroundColor: '#f0f0f0' }}>
          
          {/* RESOLUTION OVERLAYS */}
          {gamePhase === 'RESOLUTION' && (
            <>
              {renderResolutionToggle()}
              {renderResolutionMapOverlay()}
              {/* Debug: Show raw resolvedMapNodes as JSON in results view */}
              {resolutionView === 'results' && resolvedMapNodes.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: 60,
                  right: 10,
                  zIndex: 200,
                  background: 'rgba(255,255,255,0.97)',
                  border: '1px solid #1976d2',
                  borderRadius: '8px',
                  padding: '12px',
                  maxWidth: '400px',
                  maxHeight: '300px',
                  overflow: 'auto',
                  fontSize: '12px',
                  color: '#222',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}>
                  <div style={{fontWeight:'bold', marginBottom:'6px', color:'#1976d2'}}>Debug: Resolved Map Data</div>
                  <pre style={{margin:0, whiteSpace:'pre-wrap', wordBreak:'break-all'}}>{JSON.stringify(resolvedMapNodes, null, 2)}</pre>
                </div>
              )}
            </>
          )}

          {/* Setup Progress Header */}
          {gamePhase === 'SETUP' && (
            <div style={{ backgroundColor: '#fff3e0', border: '2px solid #ff9800', borderRadius: '8px', padding: '15px', margin: '10px', textAlign: 'center' }}>
              <h4 style={{ color: '#ff9800', margin: '0 0 10px 0' }}>🎯 Army Deployment Phase</h4>
              <p><strong>Armies remaining: {armiesToPlace}/6</strong></p>
              {armiesToPlace === 0 && <p style={{ color: '#4CAF50', fontWeight: 'bold' }}>✅ Setup Complete! Starting game...</p>}
            </div>
          )}
          
          <h3 style={{ textAlign: 'center', margin: '10px 0' }}>Cyber Territory Map - Game ID: {gameId}</h3>
          
          <svg width="100%" height="500" style={{ backgroundColor: '#e8f4f8', border: '2px solid #333' }}>
            {(() => {
              // Use resolvedMapNodes for RESOLUTION/results view, otherwise mapNodes
              const nodes = (gamePhase === 'RESOLUTION' && resolutionView === 'results' && resolvedMapNodes.length > 0)
                ? resolvedMapNodes
                : mapNodes;
              // Draw connections
              const connections = nodes.flatMap(node =>
                (node.connections || []).map(connId => {
                  const connNode = nodes.find(n => n.id === connId);
                  if (!connNode) return null;
                  return <line key={`${node.id}-${connId}`} x1={node.x} y1={node.y} x2={connNode.x} y2={connNode.y} stroke="#ccc" strokeWidth="2" />;
                }).filter(Boolean)
              );
              // Draw orders and pending visuals only if NOT in results view
              const showOrders = !(gamePhase === 'RESOLUTION' && resolutionView === 'results');
              const orders = showOrders ? myOrders.map(order => {
                const sourceNode = nodes.find(n => n.id === order.source);
                const targetNode = order.target ? nodes.find(n => n.id === order.target) : null;
                if (!sourceNode) return null;
                if ((order.type === 'MOVE' || order.type === 'SUPPORT') && targetNode) {
                  const labelX = sourceNode.x + 0.75 * (targetNode.x - sourceNode.x);
                  const labelY = sourceNode.y + 0.75 * (targetNode.y - sourceNode.y) - 10;
                  let orderLabel = order.type === 'MOVE' ? `a${order.amount || 1}` : `s${order.amount || 1}`;
                  return (
                    <g key={order.id}>
                      <line
                        x1={sourceNode.x} y1={sourceNode.y} x2={targetNode.x} y2={targetNode.y}
                        stroke={order.type === 'MOVE' ? '#FFC107' : '#4CAF50'}
                        strokeWidth={order.type === 'MOVE' ? 4 : 3}
                        markerEnd={order.type === 'MOVE' ? 'url(#arrowhead)' : 'url(#arrowhead-green)'}
                      />
                      <text x={labelX} y={labelY} fontSize="16" fontWeight="bold" fill={order.type === 'MOVE' ? '#FFC107' : '#4CAF50'} textAnchor="middle">{orderLabel}</text>
                    </g>
                  );
                }
                if (order.type === 'HOLD' && targetNode) {
                   // HOLD rendering logic...
                   return null; // Simplified for brevity as per original logic
                }
                return null;
              }) : null;
              const pendingVisual = showOrders && pendingOrder && pendingOrder.source && !pendingOrder.target && selectedTerritory === pendingOrder.source
                ? <circle cx={nodes.find(n => n.id === pendingOrder.source)?.x} cy={nodes.find(n => n.id === pendingOrder.source)?.y} r="30" fill="none" stroke="#FFC107" strokeWidth="2" strokeDasharray="4,4"/>
                : null;
              // Markers
              const markers = (
                <defs>
                  <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="28" refY="3.5" orient="auto"><polygon points="0 0, 10 3.5, 0 7" fill="#FFC107" /></marker>
                  <marker id="arrowhead-green" markerWidth="10" markerHeight="7" refX="28" refY="3.5" orient="auto"><polygon points="0 0, 10 3.5, 0 7" fill="#4CAF50" /></marker>
                </defs>
              );
              // Nodes
              // Attach tooltip to SVG root
              const nodeCircles = nodes.map((node) => {
                const order = myOrders.find(o => o.source === node.id);
                const isMine = node.owner === playerId;
                return (
                  <g key={node.id}
                    onMouseEnter={() => setHoveredNode(node)}
                    onMouseLeave={() => setHoveredNode(null)}
                  >
                    <circle
                      cx={node.x} cy={node.y} r="25"
                      fill={getTerritoryColor(node)}
                      stroke={node.id === selectedTerritory ? '#2196F3' : '#333'}
                      strokeWidth={node.id === selectedTerritory ? '3' : '1'}
                      onClick={() => handleTerritoryClick(node.id)}
                      style={{ cursor: 'pointer' }}
                    />
                    <text x={node.x} y={node.y - 30} textAnchor="middle" fontSize="10" fill="#333" fontWeight="bold">{node.name}</text>
                    <text x={node.x} y={node.y + 5} textAnchor="middle" fontSize="12" fill="white" fontWeight="bold">{isMine ? (node.armies || 0) : (node.owner ? '?' : '')}</text>
                    {gamePhase === 'PRODUCTION' && isMine && <text x={node.x + 18} y={node.y + 18} fontSize="16" fill="#43a047" fontWeight="bold">+1</text>}
                    {order && showOrders && <text x={node.x + 15} y={node.y - 15} fontSize="14">{order.type === 'HOLD' ? '🛡️' : order.type === 'MOVE' ? '➡️' : order.type === 'SUPPORT' ? '🤝' : ''}</text>}
                  </g>
                );
              });
              // Tooltip overlay (HTML, not SVG, for better formatting)
              const tooltip = hoveredNode ? (
                <div style={{
                  position: 'absolute',
                  left: hoveredNode.x + 30,
                  top: hoveredNode.y,
                  zIndex: 300,
                  background: 'rgba(30,30,30,0.97)',
                  color: '#fff',
                  border: '1px solid #1976d2',
                  borderRadius: '8px',
                  padding: '10px',
                  maxWidth: '350px',
                  fontSize: '12px',
                  pointerEvents: 'none',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
                }}>
                  <div style={{fontWeight:'bold', marginBottom:'4px', color:'#90caf9'}}>{hoveredNode.name} (ID: {hoveredNode.id})</div>
                  <pre style={{margin:0, whiteSpace:'pre-wrap', wordBreak:'break-all'}}>{JSON.stringify(hoveredNode, null, 2)}</pre>
                </div>
              ) : null;
              return <><>{connections}{orders}{pendingVisual}{markers}{nodeCircles}</>{tooltip}</>;
            })()}
          </svg>
        </div>

        {/* Side Panel */}
        <div style={{ flex: 1, padding: '20px', backgroundColor: '#f9f9f9', borderLeft: '2px solid #ddd' }}>
          
          {/* Player Legend */}
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h4 style={{ margin: '0 0 10px 0', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>Factions</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {players.map(p => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: FACTION_COLORS[p.color] || p.color || '#999' }} />
                  <span style={{ fontWeight: p.id === playerId ? 'bold' : 'normal', color: p.id === playerId ? '#2196F3' : '#333' }}>{p.name}</span>
                </div>
              ))}
            </div>
          </div>

          <h3>{gamePhase === 'SETUP' ? 'Army Placement' : `Phase: ${gamePhase}`}</h3>
          
          <button onClick={() => { if (window.confirm('Restart game?')) socket.emit('restartNegotiatorGame', { gameId }); }} style={{ width: '100%', padding: '8px', marginBottom: '15px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>🔄 Restart Game</button>
          
          {gamePhase !== 'SETUP' && (
            <div style={{ padding: '15px', backgroundColor: getPhaseColor(gamePhase) + '20', border: `2px solid ${getPhaseColor(gamePhase)}`, borderRadius: '8px', marginBottom: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ margin: '0', color: getPhaseColor(gamePhase) }}>{getPhaseIcon(gamePhase)} {gamePhase}</h4>
                <span style={{ fontWeight: 'bold', fontSize: '18px' }}>{formatTime(phaseTimeLeft)}</span>
              </div>
            </div>
          )}
          
          {gamePhase === 'SETUP' && (
            <div>
              <div style={{ padding: '15px', backgroundColor: '#e3f2fd', border: '2px solid #2196f3', borderRadius: '8px', marginBottom: '15px' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#1565c0' }}>🎯 Setup Phase</h4>
                <p>You have <strong>{armiesToPlace}</strong> armies to place</p>
              </div>
              <div>
                <h4>Your Territories:</h4>
                {myTerritories.map(tid => {
                  const t = mapNodes.find(n => n.id === tid);
                  if (!t) return null;
                  return (
                    <div key={tid} onClick={() => handleTerritoryClick(tid)} style={{ padding: '8px', margin: '5px 0', backgroundColor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '5px', cursor: 'pointer' }}>
                      <strong>{t.name}</strong><br /><small>Armies: {t.armies}</small>
                    </div>
                  );
                })}
              </div>
              {armiesToPlace === 0 && !setupComplete && (
                <button onClick={() => { setSetupComplete(true); if (socket?.connected) socket.emit('negotiatorSetupComplete', { gameId, playerId }); }} style={{ width: '100%', padding: '12px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', marginTop: '15px' }}>✅ Confirm Setup</button>
              )}
            </div>
          )}

          {/* Order Entry UI */}
          {gamePhase === 'DIPLOMACY' && (
            <div>
              {selectedTerritory ? (
                <div style={{ padding: '15px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px' }}>
                  <h4 style={{ margin: '0 0 10px 0' }}>{mapNodes.find(n => n.id === selectedTerritory)?.name}</h4>
                  {myTerritories.includes(selectedTerritory) ? (
                    <>
                      {pendingOrder && pendingOrder.source === selectedTerritory ? (
                        <div>
                          <div style={{ padding: '8px', backgroundColor: '#fff3e0', border: '1px solid #ff9800', borderRadius: '4px', marginBottom: '10px' }}>
                            <strong>Action:</strong> {pendingOrder.type}<br/>
                            {pendingOrder.target ? <span><strong>Target:</strong> {mapNodes.find(n => n.id === pendingOrder.target)?.name}</span> : <span style={{ color: '#ff9800' }}>Select target...</span>}
                          </div>
                          {(pendingOrder.type === 'MOVE' || pendingOrder.type === 'SUPPORT' || pendingOrder.type === 'HOLD') && (
                            <div style={{ marginBottom: '10px' }}>
                               <label>Amount: </label>
                               <input type="number" min={1} value={pendingOrderAmount} onChange={e => setPendingOrderAmount(Number(e.target.value))} style={{width:'50px'}} />
                            </div>
                          )}
                          {pendingOrder.target && (
                            <div style={{ display: 'flex', gap: '10px' }}>
                              <button onClick={confirmPendingOrder} style={{ flex: 1, padding: '8px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px' }}>Confirm</button>
                              <button onClick={cancelPendingOrder} style={{ flex: 1, padding: '8px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '4px' }}>Cancel</button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <button onClick={() => handleCreateOrder('MOVE')} disabled={!!pendingOrder} style={{ padding: '8px', backgroundColor: '#ff9800', border: 'none', borderRadius: '4px' }}>Move / Attack</button>
                          <button onClick={() => handleCreateOrder('SUPPORT')} disabled={!!pendingOrder} style={{ padding: '8px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '4px' }}>Support</button>
                          <button onClick={() => handleCreateOrder('HOLD')} disabled={!!pendingOrder} style={{ padding: '8px', backgroundColor: '#9E9E9E', color: 'white', border: 'none', borderRadius: '4px' }}>Hold</button>
                        </div>
                      )}
                    </>
                  ) : (
                    <p>Not your territory.</p>
                  )}
                </div>
              ) : (
                <div style={{ padding: '15px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px' }}>
                  <h4>Select a Territory</h4>
                </div>
              )}
            </div>
          )}

          {/* Orders List */}
          <div style={{ marginTop: '20px' }}>
            <h4>My Orders:</h4>
            {myOrders.map(order => (
              <div key={order.id} style={{ padding: '8px', margin: '5px 0', backgroundColor: '#f5f5f5', border: '1px solid #ddd', borderRadius: '5px', display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <strong>{mapNodes.find(n => n.id === order.source)?.name}</strong><br/>
                  {order.type} {order.target ? `to ${mapNodes.find(n => n.id === order.target)?.name}` : ''}
                  {order.amount && <span> (x{order.amount})</span>}
                </div>
                <button onClick={() => deleteOrder(order.id)} style={{ border: 'none', background: 'none', color: 'red', cursor: 'pointer' }}>×</button>
              </div>
            ))}
            {['PRODUCTION', 'DIPLOMACY', 'ORDERS'].includes(gamePhase) && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <button onClick={handleDoneClick} style={{ padding: '12px 24px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', fontSize: '16px', cursor: 'pointer' }}>✅ Done</button>
              </div>
            )}
          </div>

          <button onClick={onBackToMenu} style={{ width: '100%', marginTop: '20px', padding: '10px', backgroundColor: '#666', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>← Back to Menu</button>
        </div>
      </div>
    </div>
  );
};

export default NegotiatorGame;